import axios from "axios";
import { MAIN_URL } from "../../Configs/Urls";
import { store } from "../MainStore/Store";

// MainRuleController::getFareRules()'s own frontend call — previously unreachable from this
// app at all (the Review page's "VIEW FARE RULES" popup just dumped the already-embedded, lighter
// Data.FareFamilies.FFList[ffIndex].MiniRules field via a raw <ReactJson>, never calling this
// endpoint — found via explicit trace, "ek baar yeh api call hone ek frontend mein deeply dekho").
// `type` is one of "1Row"/"2Row"/"3Row"/"4Row" (single combined item, Sequence has exactly 1
// entry) or "2RowD"/"3RowD"/"4RowD" (N independent legs, Sequence has exactly N entries) — see
// MainRuleController::getFareRules()'s own docblock for the full Type enum. `sequence` is the
// array of {Source, Data, Ids, Travellers, FareClass} entries built by
// buildFareRulesSequenceEntry() (Utils/FareRulesHelpers.js).
export const fetchFareRules = async (type, sequence) => {
  try {
    const response = await axios.post(`${MAIN_URL}flights/tripprizer/getFareRules`, {
      Type: type,
      Sequence: sequence,
    });
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const debouncedSearch = async (
  value,
  setSearchLoading,
  setSearchedAirports
) => {
  if (value.length === 0) {
    setSearchedAirports([]);
    return;
  }
  try {
    setSearchLoading(true);
    const response = await axios.get(
      `${MAIN_URL}masters/flights/airports/search_v1?search=${value}`
    );
    const data = response.data.data;
    console.log("data after seatch her si : ", data);

    if (data?.length > 0) {
      setSearchedAirports(data);
    } else {
      setSearchedAirports([]);
    }
    setSearchLoading(false);
  } catch (error) {
    console.error("Error fetching search results:", error);
    setSearchLoading(false);
  }
};

// Exact-code lookup — same `code_exact` endpoint/method the admin panel's
// AirportAutocomplete already uses (masters/flights/airports?code_exact=XXX).
// This is a dedicated DB equality match, so it never depends on where the
// airport ranks inside the generic text-search results.
export const fetchAirportByExactCode = async (code) => {
  try {
    const response = await axios.get(`${MAIN_URL}masters/flights/airports`, {
      params: { code_exact: code, per_page: 1 },
    });
    const airport = response.data?.data?.[0];
    if (!airport) return null;

    return {
      id: airport.id,
      airport_code: airport.airport_code,
      airport_name: airport.airport_name,
      city_name: airport.city?.city_name ?? "",
      country_name: airport.airport_country_name ?? airport.country?.name ?? "",
      country_code: airport.country?.code ?? "",
    };
  } catch (error) {
    console.error("Error fetching airport by exact code:", error);
    return null;
  }
};

export async function fetchPopularAirports() {
  try {
    const response = await fetch(MAIN_URL + "masters/flights/popular-cities");
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

// `currency` MUST be passed — without it the backend returns every currency's fares mixed
// together for this O&D (e.g. some dates genuinely stored in EUR, others in INR, whichever
// pipeline/search happened to record each date's cheapest fare), and CalendarGridGenerator's
// single dealFareCurrency->vc conversion rate then gets wrongly applied to ALL of them,
// producing nonsense numbers for whichever dates weren't actually in that currency. Callers
// must pass the same `dealFareCurrency` useDisplayCurrency() resolves (its own homepage
// fallback is 'INR' when there's no results-page search yet) — see
// MainSearchController's $calendarCurrency/$SearchRequestForCal for how the in-results
// calendar (ResultBar4/CalenderBar) already does this correctly.
export const fetchCalendarValues = async (origin, destination, currency = "INR") => {
  try {
    const response = await axios.get(
      `${MAIN_URL}masters/flights/search-calendar-fares`,
      {
        params: {
          origin: origin,
          destination: destination,
          currency: currency,
        },
      }
    );

    const transformedData = response.data.reduce((acc, item) => {
      const date = new Date(item.date);
      const formattedDate = `${date.getDate().toString().padStart(2, "0")}-${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}-${date.getFullYear()}`;
      acc[formattedDate] = parseFloat(item.price);
      return acc;
    }, {});

    return transformedData;
  } catch (error) {
    throw error;
  }
};

// $signal (an AbortSignal, from SearchTheFlights's thunkAPI.signal) lets Redux Toolkit's own
// thunk-cancellation reach all the way down to the actual network request — dispatching
// SearchTheFlights(...).abort() (see ResultPage.jsx's back/forward/tab-close handling) aborts
// this signal, axios cancels the in-flight XHR immediately, and the thunk resolves as 'aborted'
// instead of waiting for a response that's no longer needed.
export const fetchTheFlights = async (params, signal) => {
  try {
    // IsAdminSearch drives the fee-resolution engine's subadmin step (backend: isThisSearchByAdmin) —
    // only true when an admin is actually SSO-logged into this portal (user.isAdminLogin), never
    // for a real customer search.
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/search`,
      { ...params, IsAdminSearch: isAdminLogin },
      { signal }
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Admin-only "combined total fees" popup on round-trip domestic results (ResultBarSection.jsx's
// mode===2 "Total cost" bar) — sends the two currently-selected Going/Returning items so the
// backend can run the same TP-Connect -> traveller-multiply -> Markup Pass Settings -> Subadmin
// pipeline DOM-OW-CA's two legs already get combined through (see
// MainSearchController::roundDomesticCombinedFees()'s docblock).
// pipelineId is the switched pipeline this WHOLE search ran under (SearchParameters.User.Country.
// pipeline_id, or "x" for Global) — sent instead of relying on the two items' own Source.api_source,
// since anonymizeResultIds() (MainSearchHelpers.php) strips api_source out of every response entry
// before it ever reaches the frontend, so a round-tripped item never actually carries it. The
// backend builds a synthetic 'switched' api_source from this pipelineId directly (see
// resolveRoundDomesticCombinedFees()'s docblock) — also more correct than trusting a per-item
// api_source would be, since combined TP-Connect/Subadmin settings should follow the search's own
// anchor pipeline either way, not whichever pipeline actually served one specific leg.
// goingFFIndex/returningFFIndex (both optional, default 0 — fully backward compatible with every
// existing caller that doesn't pass them) — per explicit instruction ("dynamic farefamily
// selection wise"): which of Going's/Returning's own FareFamilies.FFList the admin/user actually
// picked, not just the default FFList[0]. DOM-RD/DOM-RD-NR's own flat items already have EVERY
// fare family's Markup precomputed at search time (resolveFareFeesForEveryFareFamily() — see
// MainSearchController::roundDomesticCombinedFees()'s own docblock), so the backend just swaps
// the chosen family into FFList[0] before combining — no extra Markup recompute needed, unlike
// DOM-OW-CA/INT-OW-CA's own combined-fees endpoints.
// Migrated to the same ResultSetId+ResultId-only pattern as fetchDomOwCaCombinedFees() — per
// explicit instruction ("round domestic mein bhi yeh sab cheezein karo"). goingResultId/
// returningResultId identify WHICH two independently-picked result items this is (not which fare
// family of a fixed pair, like DOM-OW-CA) — lookupSavedResultNodeByAnonId() finds each one
// server-side from the saved row.
export const fetchRoundDomesticCombinedFees = async (
  resultSetId,
  goingResultId,
  returningResultId,
  fare,
  travellers,
  pipelineId,
  goingFFIndex = 0,
  returningFFIndex = 0
) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/round-domestic-combined-fees`,
      {
        ResultSetId: resultSetId,
        GoingResultId: goingResultId,
        ReturningResultId: returningResultId,
        GoingFFIndex: goingFFIndex,
        ReturningFFIndex: returningFFIndex,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// DOM-OW-CA's "MORE FARE OPTIONS" popup — unlike fetchRoundDomesticCombinedFees() (which picks
// between different RESULT ITEMS), this picks between different FARE FAMILIES of the SAME two
// connecting-airport legs.
//
// leg1ResultId/leg2ResultId — NOT the full Connections[0]/[1] objects anymore (those used to be
// sent whole, every OTHER fare family/every Segment included, just to recompute ONE chosen
// family's total) — per explicit instruction ("puri ki puri leg ja rahi hai backend mein as
// payload... security wise behtar rahega"), only each leg's own already-anonymized ResultId is
// sent now (resultSetId required alongside it so the backend knows which saved row to look
// in). Company/ApiSource/the rest of each leg's own data is reconstructed server-side from the
// already-saved FlightSearchResult row (lookupSavedResultNodeByAnonId(), MainSearchHelpers.php),
// same pattern the Hold migration already established. leg1FFIndex/leg2FFIndex still say which of
// each leg's own FFList the admin/user actually selected (see MainSearchController::
// domOwCaCombinedFees()'s docblock).
//
// resultSetId, NOT searchId — the saved row's own DB id (from the search response's
// 'ResultSetId' field), found to be the right lookup key via explicit trace: SearchId was
// never actually ambiguous (globally-unique strings), but looking a row up by it meant a full
// JSON-column table scan on every call, where this is an instant, indexed primary-key lookup.
//
// Trade-off (accepted, per explicit instruction): this now depends on the same 10-minute cache
// TTL Hold does — a fare-family change more than 10 minutes into browsing a result can get back a
// RES_EXPIRED error (code/Message same shape as Hold's own), where it never could before (the old
// full-object request only ever used the frontend's own in-memory copy, never the DB).
export const fetchDomOwCaCombinedFees = async (
  resultSetId,
  leg1ResultId,
  leg1FFIndex,
  leg2ResultId,
  leg2FFIndex,
  fare,
  travellers,
  pipelineId
) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/dom-ow-ca-combined-fees`,
      {
        ResultSetId: resultSetId,
        Leg1ResultId: leg1ResultId,
        Leg1FFIndex: leg1FFIndex,
        Leg2ResultId: leg2ResultId,
        Leg2FFIndex: leg2FFIndex,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Review Page's own "Upgrade Fare" popup, DOM-OW-CA — live discount preview mirroring
// fetchDomOwCaCombinedFees() above, but for an ALREADY-HELD booking: only itenaryId + FFIndexes
// (which this popup already has/sends to HoldTheFlight() itself) — the backend resolves each
// leg's ResultId, the saved search row, and Fare/Travellers/pipeline_id all itself from the Hold
// row (see previewDomOwCaCombinedFeesForHoldUpgrade()'s own docblock) — per explicit instruction
// ("Result Page jaisa hi live preview chahiye yahan bhi").
export const fetchReviewDomOwCaCombinedFeesPreview = async (itenaryId, ffIndexes) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/review-dom-ow-ca-combined-fees-preview`,
      {
        itenaryId,
        FFIndexes: ffIndexes,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// INT-OW-CA's equivalent of fetchReviewDomOwCaCombinedFeesPreview() above — same contract, just
// hits International's own endpoint (see MainSearchController::reviewIntOwCaCombinedFeesPreview()'s
// docblock).
export const fetchReviewIntOwCaCombinedFeesPreview = async (itenaryId, ffIndexes) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/review-int-ow-ca-combined-fees-preview`,
      {
        itenaryId,
        FFIndexes: ffIndexes,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Round Domestic (DOM-RD/DOM-RD-NR)'s equivalent of fetchReviewDomOwCaCombinedFeesPreview() above
// — same "already-held booking" contract (only itenaryId + FFIndexes: [goingIndex, returningIndex]),
// just hits Round Domestic's own endpoint (see MainSearchController::
// reviewRoundDomesticCombinedFeesPreview()'s docblock). Added to close the Review Page "Upgrade
// Fare" popup's own live-preview gap for Round Domestic — BottomBar/index.js was previously
// mis-routing this case to fetchReviewDomOwCaCombinedFeesPreview() (always rejected server-side,
// DataFormat check), so no live preview was ever actually shown here before.
export const fetchReviewRoundDomesticCombinedFeesPreview = async (itenaryId, ffIndexes) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/review-round-domestic-combined-fees-preview`,
      {
        itenaryId,
        FFIndexes: ffIndexes,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// INT-RD-OWOW/INT-RD-OWOW-NR's equivalent of fetchReviewRoundDomesticCombinedFeesPreview() above
// — same "already-held booking" contract (only itenaryId + FFIndexes: [leg1Index, leg2Index]),
// just hits INT-RD-OWOW's own endpoint (see MainSearchController::
// reviewIntRdOwowCombinedFeesPreview()'s docblock). Added to close the same Review Page "Upgrade
// Fare" popup live-preview gap Round Domestic had — BottomBar/index.js was mis-routing this case
// to fetchReviewDomOwCaCombinedFeesPreview() (always rejected server-side, DataFormat check), so
// no live preview was ever actually shown here before.
export const fetchReviewIntRdOwowCombinedFeesPreview = async (itenaryId, ffIndexes) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/review-int-rd-owow-combined-fees-preview`,
      {
        itenaryId,
        FFIndexes: ffIndexes,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Multiway Domestic (DOM-ML-2/3/4 + -NR) equivalent of fetchReviewRoundDomesticCombinedFeesPreview()
// above — same already-held-booking contract, but FFIndexes carries N entries (one per leg).
export const fetchReviewMultiwayDomesticCombinedFeesPreview = async (itenaryId, ffIndexes) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/review-multiway-domestic-combined-fees-preview`,
      {
        itenaryId,
        FFIndexes: ffIndexes,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// INT-OW-CA's equivalent of fetchDomOwCaCombinedFees() above — same contract, just hits
// International's own endpoint (see MainSearchController::intOwCaCombinedFees()'s docblock).
// Migrated to the same ResultSetId+ResultId-only pattern as fetchDomOwCaCombinedFees() — per
// explicit instruction ("int-ow-ca ke farefamily change pe kya bhi shi kro"). See that function's
// own comment for the full reasoning/trade-off (now depends on the 1-hour prune window, same as
// Hold).
export const fetchIntOwCaCombinedFees = async (
  resultSetId,
  leg1ResultId,
  leg1FFIndex,
  leg2ResultId,
  leg2FFIndex,
  fare,
  travellers,
  pipelineId
) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/int-ow-ca-combined-fees`,
      {
        ResultSetId: resultSetId,
        Leg1ResultId: leg1ResultId,
        Leg1FFIndex: leg1FFIndex,
        Leg2ResultId: leg2ResultId,
        Leg2FFIndex: leg2FFIndex,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// INT-RD-OWOW's Combined-view equivalent of fetchIntOwCaCombinedFees() above — a single
// already-paired item (Connections[0]/[1] baked in at search time), NOT the Split view (that's
// fetchRoundInternationalCombinedFees() below, for separate Going/Returning ITEMS). Same contract
// otherwise — hits MainSearchController::intRdOwowCombinedFees()'s own dedicated endpoint, NOT
// fetchDomOwCaCombinedFees()/fetchIntOwCaCombinedFees() (BottomBar/index.js's own DataFormat
// check must route here specifically — those two don't know this DataFormat exists).
//
// Migrated to the same ResultSetId+ResultId-only pattern as fetchDomOwCaCombinedFees()/
// fetchIntOwCaCombinedFees() — per explicit instruction ("ab combined wale pe kardo").
export const fetchIntRdOwowCombinedFees = async (
  resultSetId,
  leg1ResultId,
  leg1FFIndex,
  leg2ResultId,
  leg2FFIndex,
  fare,
  travellers,
  pipelineId
) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/int-rd-owow-combined-fees`,
      {
        ResultSetId: resultSetId,
        Leg1ResultId: leg1ResultId,
        Leg1FFIndex: leg1FFIndex,
        Leg2ResultId: leg2ResultId,
        Leg2FFIndex: leg2FFIndex,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Same as fetchRoundDomesticCombinedFees() above, for Round International's INT-RD-OWOW Split/
// "Select Your Own Flights" view (see MainSearchController::roundInternationalCombinedFees()'s
// docblock) — the button is the SAME shared component in ResultBarSection.jsx's mode===2 branch
// (rendered for both SearchResultType 3 and 4), CombinedFeesPopup.jsx picks which of these two
// fetch functions to call based on its own isInternational prop.
// goingFFIndex/returningFFIndex — same dynamic fare-family selection as
// fetchRoundDomesticCombinedFees() above ("ab international round banao"): the SAME existing
// FFSelected picker (FareFamilyContent.jsx) already lets the admin/user choose a fare family for
// Going (FFSelected[0]) and Returning (FFSelected[1]); these just forward those indices.
//
// Migrated to the same ResultSetId+ResultId-only pattern as fetchRoundDomesticCombinedFees() —
// per explicit instruction ("select your own flight mein fares/fare family change karte hain,
// us API ko bhi"). goingResultId/returningResultId identify WHICH two independently-picked
// result items this is — lookupSavedResultNodeByAnonId() finds each one server-side.
export const fetchRoundInternationalCombinedFees = async (resultSetId, goingResultId, returningResultId, fare, travellers, pipelineId, goingFFIndex = 0, returningFFIndex = 0) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/round-international-combined-fees`,
      {
        ResultSetId: resultSetId,
        GoingResultId: goingResultId,
        ReturningResultId: returningResultId,
        GoingFFIndex: goingFFIndex,
        ReturningFFIndex: returningFFIndex,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Same as fetchRoundDomesticCombinedFees()/fetchRoundInternationalCombinedFees() above, for
// Multiway Domestic's 2/3/4-row results (see MainSearchController::multiwayDomesticCombinedFees()'s
// docblock) — generalized to N legs (an array, "Legs") instead of a fixed Going/Returning pair.
// legFFIndices — same dynamic fare-family selection as fetchRoundDomesticCombinedFees() above
// ("multi 2,3,4 domestic ko bhi dynamic fare family selection se banao"): an array parallel to
// legItems (one FFSelected index per row, same order), optional — defaults to all-0s.
//
// Migrated to the same ResultSetId+ResultId-only pattern as fetchRoundDomesticCombinedFees() —
// per explicit instruction ("isi tarha ke sab changes multiway domestic mein bhi karo"). legItems
// still accepted as full objects (callers unchanged) — only each leg's own ResultId is sent.
export const fetchMultiwayDomesticCombinedFees = async (resultSetId, legItems, fare, travellers, pipelineId, legFFIndices = []) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/multiway-domestic-combined-fees`,
      {
        ResultSetId: resultSetId,
        LegResultIds: (legItems || []).map((leg) => leg?.ResultId),
        LegFFIndices: legFFIndices,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Same as fetchMultiwayDomesticCombinedFees() above, for Multiway International's 2/3/4-row
// results (see MainSearchController::multiwayInternationalCombinedFees()'s docblock).
// legFFIndices — same dynamic fare-family selection as fetchMultiwayDomesticCombinedFees() above
// ("multi 2,3,4 international ko bhi dynamic fare family selection se banao"): an array parallel
// to legItems (one FFSelected index per row), optional — defaults to all-0s.
//
// Migrated to the same ResultSetId+ResultId-only pattern as fetchIntOwCaCombinedFees() —
// per explicit instruction ("isi tarha ke sab changes international multi 2,3,4 mein bhi karo").
// legItems is still accepted as full objects (so callers don't need to change) — only each leg's
// own ResultId is actually sent, extracted here.
export const fetchMultiwayInternationalCombinedFees = async (resultSetId, legItems, fare, travellers, pipelineId, legFFIndices = []) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/multiway-international-combined-fees`,
      {
        ResultSetId: resultSetId,
        LegResultIds: (legItems || []).map((leg) => leg?.ResultId),
        LegFFIndices: legFFIndices,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// INT-ML-OWOW-{row}'s Combined-view equivalent of fetchIntRdOwowCombinedFees() above — generalized
// to N legs (2-4) instead of a fixed Leg1/Leg2 pair, per explicit instruction ("multi 2,3,4
// international ko bhi dynamic fare family selection se banao"). NOT the same thing as
// fetchMultiwayInternationalCombinedFees() above (that's the Split view's separate standalone row
// items) — this is for a single already-paired Combined-view item (Connections[0..N-1]). Hits
// MainSearchController::intMlOwowCombinedFees()'s own dedicated endpoint.
//
// Migrated to the same ResultSetId+ResultId-only pattern — per explicit instruction ("isi tarha
// ke sab changes international multi 2,3,4 mein bhi karo").
export const fetchIntMlOwowCombinedFees = async (resultSetId, legItems, legFFIndices, fare, travellers, pipelineId) => {
  try {
    const isAdminLogin = store.getState().user.isAdminLogin;
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/int-ml-owow-combined-fees`,
      {
        ResultSetId: resultSetId,
        LegResultIds: (legItems || []).map((leg) => leg?.ResultId),
        LegFFIndices: legFFIndices,
        Fare: fare,
        Travellers: travellers,
        User: { Country: { pipeline_id: pipelineId } },
        IsAdminSearch: isAdminLogin,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const HoldTheFlight = async (params) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/hold`,
      params
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const SellTheFlightAPI = async (params) => {
  // alert("called")
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/sell`,
      params
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const callContinue1 = async (params) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/continue_1`,
      params
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const callContinue2 = async (params) => {
  // ancellariesCodes

  console.log("here at cval continue2  the params is : ", params);

  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/continue_2`,
      params
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const callAncellaries = async (params) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/ancellaries`,
      params
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Lazy, tab-click-driven per-category reads — straight off the snapshot callAncellaries()'s own
// backend route (persist.ancellaries middleware) already saved for this booking, no fresh
// provider call. GET + ?BookingId=... — see MainAncellariesController::getSavedSeats/Meals/Bags()
// / getSavedCategory()'s own docblock. `params` is {BookingId, SearchId} — same shape every
// caller in ReviewPage-Flights-SLICE.js's CallSeatsOnly/CallMealsOnly/CallBagsOnly already builds
// (SearchId travels along even though the backend only reads BookingId here, same as it's
// carried for no reason elsewhere too — harmless).
export const callSavedSeats = async (params) => {
  try {
    const response = await axios.get(`${MAIN_URL}flights/tripprizer/ancellaries/seats`, {
      params,
    });
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const callSavedMeals = async (params) => {
  try {
    const response = await axios.get(`${MAIN_URL}flights/tripprizer/ancellaries/meals`, {
      params,
    });
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const callSavedBags = async (params) => {
  try {
    const response = await axios.get(`${MAIN_URL}flights/tripprizer/ancellaries/bags`, {
      params,
    });
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const callProceedToPay = async (params) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/proceedToPay`,
      params
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const GetTheFlight = async (booking_id) => {
  try {
    const response = await axios.get(
      `${MAIN_URL}bookings/flights/${booking_id}`
    );

    console.log(response);
    return response;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

export const fetchTheAirline = async (params) => {
  console.log(params);
  try {
    const response = await axios.get(
      `${MAIN_URL}masters/flights/search-airlines?search=${params}`
    );

    console.log("AirlineResponse is : ", response);

    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};

// Multiway International split (INT-ML-OWOW-2/3/4): N separately-priced legs in
// FlightDetail.Connections[]; FFIndexes carries one index per leg.
export const fetchReviewIntMlOwowCombinedFeesPreview = async (itenaryId, ffIndexes) => {
  try {
    const response = await axios.post(
      `${MAIN_URL}flights/tripprizer/review-int-ml-owow-combined-fees-preview`,
      {
        itenaryId,
        FFIndexes: ffIndexes,
      }
    );
    return response?.data;
  } catch (error) {
    console.log("the api throw error is  : ", error);
    throw error;
  }
};
